import { useState } from "react";
import { Box, Collapse, Link, Stack, Typography } from "@mui/material";
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from "@mui/icons-material";
import type { HeaderCoverage } from "./buildHeaderCoverage";

interface DetectedHeadersSummaryProps {
  coverage: HeaderCoverage;
}

/**
 * Inline summary of detected-header coverage for the Mapping tab.
 *
 * Renders a single line summarizing how many source columns the parser
 * detected and how many were mapped. When some columns were left unmapped,
 * the count becomes an interactive toggle that expands the unmapped header
 * names. Surfaces a real diagnostic question reviewers ask — "did the
 * parser see the column I expected?" — without forcing them to dig into the
 * raw transformation data.
 *
 * Returns `null` when no headers were detected (e.g. the parser didn't
 * populate `processing_metadata.detected_headers`). The caller doesn't need
 * to guard.
 */
export const DetectedHeadersSummary = ({ coverage }: DetectedHeadersSummaryProps) => {
  const total = coverage.mapped.length + coverage.unmapped.length;
  const [expanded, setExpanded] = useState(false);

  if (total === 0) return null;

  const allMapped = coverage.unmapped.length === 0;

  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.25 }}>
        <Typography variant="body2" color="text.secondary">
          <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
            {total}
          </Box>{" "}
          column{total !== 1 ? "s" : ""} detected
        </Typography>
        <Sep />
        <Typography variant="body2" color="text.secondary">
          <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
            {coverage.mapped.length}
          </Box>{" "}
          mapped
        </Typography>
        {allMapped ? (
          <>
            <Sep />
            <Typography variant="body2" color="success.main" fontWeight={500}>
              all mapped
            </Typography>
          </>
        ) : (
          <>
            <Sep />
            <Link
              component="button"
              type="button"
              onClick={() => setExpanded((v) => !v)}
              underline="hover"
              sx={{
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
              }}
            >
              {coverage.unmapped.length} unmapped
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Link>
          </>
        )}
      </Stack>
      <Collapse in={expanded && !allMapped}>
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Source columns the parser saw but no mapping consumed:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {coverage.unmapped.map((header) => (
              <Box
                key={header}
                component="code"
                sx={{
                  fontSize: "0.75rem",
                  px: 0.75,
                  py: 0.25,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 0.75,
                  bgcolor: "background.paper",
                  fontFamily: "monospace",
                }}
              >
                {header}
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const Sep = () => (
  <Box component="span" sx={{ color: "text.disabled" }} aria-hidden="true">
    ·
  </Box>
);
